//Types
import { Order } from "@/types/supabase";

// Libs
import { useState, useEffect, useRef } from "react";
import clsx from "clsx";

// Hooks
import { useOrdersApi } from "@/hooks/useOrdersApi";
import { useOrdersStore } from "./use-orders-store";

// Components
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/atoms/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/atoms/dialog";
import { Button } from "@/components/atoms/button";

// Icons
import { Check, ChevronRight } from "lucide-react";
import OrderCancellationModal from "@/components/modals/OrderCancellationModal";
import { toastHandler } from "@/lib/ToastHandler";

export default function OrderStatusChange({ order }: { order: Order }) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [isSelectDisabled, setIsSelectDisabled] = useState(false);
  const [shouldDisableAfterClose, setShouldDisableAfterClose] = useState(false);
  const [dialogFromStatus, setDialogFromStatus] = useState<
    Order["status"] | ""
  >("");
  const [pendingStatusToApply, setPendingStatusToApply] = useState<
    Order["status"] | null
  >(null);
  const [currentStatus, setCurrentStatus] = useState<Order["status"]>(
    order.status,
  );

  // Ref to capture order state when confirming change to avoid stale closures
  const orderRef = useRef<Order>(order);

  const { updateOrderStatus, loading } = useOrdersApi();
  const setOrderDetailDirect = useOrdersStore((s) => s.setOrderDetailDirect);

  useEffect(() => {
    setCurrentStatus(order.status);
    // Keep ref updated with latest order data
    orderRef.current = order;
  }, [order]);

  const statuses = [
    {
      order: 0,
      db_name: "cancelled",
      name: "Cancelada",
    },
    {
      order: 1,
      db_name: "pending",
      name: "En cola",
    },
    {
      order: 2,
      db_name: "accepted",
      name: "Asignada",
    },
    {
      order: 3,
      db_name: "sorted",
      name: "Surtida",
    },
    {
      order: 4,
      db_name: "in_transit",
      name: "En tránsito",
    },
    {
      order: 5,
      db_name: "on_site",
      name: "En sitio",
    },
    {
      order: 6,
      db_name: "completed",
      name: "Completada",
    },
  ];

  // Get the current status order to determine which step we're on
  const getCurrentStatusOrder = () => {
    const currentStatusObj = statuses.find((s) => s.db_name === currentStatus);
    return currentStatusObj?.order || 0;
  };

  const handleStatusChange = (value: string) => {
    if (value !== currentStatus) {
      // Snapshot the "from" status so it doesn't change while the dialog is open
      setDialogFromStatus(order.status);
      setSelectedStatus(value);
      setIsDialogOpen(true);
    }
  };

  // Disable the select only after the confirmation dialog has closed
  useEffect(() => {
    if (!isDialogOpen && shouldDisableAfterClose) {
      setIsSelectDisabled(true);
      setShouldDisableAfterClose(false);
    }
  }, [isDialogOpen, shouldDisableAfterClose]);

  // Apply the new status and update store AFTER the dialog is closed
  useEffect(() => {
    if (!isDialogOpen && pendingStatusToApply) {
      setCurrentStatus(pendingStatusToApply);
      setOrderDetailDirect({
        ...orderRef.current,
        status: pendingStatusToApply,
      } as Order);
      setPendingStatusToApply(null);
    }
  }, [isDialogOpen, pendingStatusToApply, setOrderDetailDirect]);

  const handleConfirmStatusChange = async () => {
    const currentStatus = order.status;
    if (selectedStatus) {
      // Capture current order state in ref before async operations
      orderRef.current = order;
      try {
        // If cancelling, modal handles it; skip direct call here
        if (selectedStatus !== "cancelled") {
          await updateOrderStatus(order.id, selectedStatus);
        }
        // Defer applying the status until after dialog closes
        setPendingStatusToApply(selectedStatus as Order["status"]);
        // Schedule disabling for completed after dialog closes
        if (selectedStatus === "completed") {
          setShouldDisableAfterClose(true);
        }
        toastHandler({
          title: "Estado actualizado correctamente",
          description: `El estado del pedido ${order.tracking_id} ha sido actualizado de ${getStatusName(currentStatus)} a ${getStatusName(selectedStatus)}`,
          variant: "success",
        });
      } catch (error) {
        // Error handling is already done in useOrdersApi hook
      } finally {
        setDialogFromStatus("");
      }
    }
    setIsDialogOpen(false);
    setSelectedStatus(null);
  };

  const handleCancelStatusChange = () => {
    setIsDialogOpen(false);
    setSelectedStatus(null);
  };

  const getStatusName = (status: string) => {
    return statuses.find((s) => s.db_name === status)?.name || status;
  };

  const StatusBadge = ({
    db_name,
    order,
  }: {
    db_name: string;
    name: string;
    order: number;
  }) => {
    const currentOrder = getCurrentStatusOrder();
    const isCompleted = order < currentOrder;
    const isCurrent = order === currentOrder;
    const isCancelled = db_name === "cancelled";
    const isFuture = order > currentOrder;

    return (
      <div
        className={clsx(
          "flex items-center gap-2 text-xs sm:text-sm transition-all duration-200",
          isCancelled
            ? "text-red-600"
            : isCurrent
              ? "text-blue-700 font-medium"
              : isCompleted
                ? "text-green-700"
                : isFuture
                  ? "text-gray-400"
                  : "text-gray-500",
        )}
      >
        <div
          className={clsx(
            "rounded-full w-5 h-5 sm:w-6 sm:h-6 grid place-items-center flex-shrink-0 transition-all duration-200",
            isCancelled
              ? "bg-red-500 shadow-sm"
              : isCurrent
                ? "bg-blue-600 shadow-md ring-2 ring-blue-200"
                : isCompleted
                  ? "bg-green-500 shadow-sm"
                  : isFuture
                    ? "border-2 border-gray-200 bg-white"
                    : "border border-gray-300 bg-white",
          )}
        >
          {isCancelled ? (
            <span className="text-xs sm:text-sm font-semibold text-white">
              ✕
            </span>
          ) : isCompleted ? (
            <Check
              size={14}
              strokeWidth={3}
              className="text-white sm:w-4 sm:h-4"
            />
          ) : isCurrent ? (
            <span className="text-xs sm:text-sm font-bold text-white">
              {order}
            </span>
          ) : (
            <span className="text-xs sm:text-sm font-medium text-gray-500">
              {order}
            </span>
          )}
        </div>
        <span
          className={clsx(
            "whitespace-nowrap transition-all duration-200",
            isCurrent && "font-medium",
          )}
        >
          {getStatusName(db_name)}
        </span>
      </div>
    );
  };

  return (
    <>
      <section
        className={clsx(
          "bg-white shadow-md rounded-2xl px-4 py-6 sm:px-6 sm:py-8 flex flex-col gap-6 xl:flex-row lg:items-center lg:justify-between",
          order.status === "cancelled" && "opacity-90 pointer-events-none",
        )}
      >
        {/* Status Progress */}
        <div className="flex flex-col gap-4 w-full lg:w-auto">
          {/* Progress indicator */}
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-gray-700">
              Estado actual: {getStatusName(currentStatus)}
            </span>
          </div>

          {/* Mobile: Vertical layout with progress line */}
          <div className="flex flex-col gap-3 sm:hidden relative">
            {/* Progress line */}

            <StatusBadge db_name={"pending"} name="En cola" order={1} />
            <StatusBadge db_name={"accepted"} name="Asignada" order={2} />
            <StatusBadge db_name={"sorted"} name="Surtida" order={3} />
            <StatusBadge db_name={"in_transit"} name="En tránsito" order={4} />
            <StatusBadge db_name={"on_site"} name="En sitio" order={5} />
            <StatusBadge db_name={"completed"} name="Completada" order={6} />
          </div>

          {/* Tablet: Horizontal with progress line */}
          <div className="hidden sm:flex flex-wrap items-center gap-3 relative w-full">
            <div className="relative z-10">
              <StatusBadge db_name={"pending"} name="En cola" order={1} />
            </div>
            <ChevronRight
              size={16}
              className="text-gray-400 hidden lg:block relative z-10"
            />
            <div className="relative z-10">
              <StatusBadge db_name={"accepted"} name="Asignada" order={2} />
            </div>
            <ChevronRight
              size={16}
              className="text-gray-400 hidden lg:block relative z-10"
            />
            <div className="relative z-10">
              <StatusBadge db_name={"sorted"} name="Surtida" order={3} />
            </div>
            <ChevronRight
              size={16}
              className="text-gray-400 hidden lg:block relative z-10"
            />
            <div className="relative z-10">
              <StatusBadge
                db_name={"in_transit"}
                name="En tránsito"
                order={4}
              />
            </div>
            <ChevronRight
              size={16}
              className="text-gray-400 hidden lg:block relative z-10"
            />
            <div className="relative z-10">
              <StatusBadge db_name={"on_site"} name="En sitio" order={5} />
            </div>
            <ChevronRight
              size={16}
              className="text-gray-400 hidden lg:block relative z-10"
            />
            <div className="relative z-10">
              <StatusBadge db_name={"completed"} name="Completada" order={6} />
            </div>
          </div>
        </div>

        {/* Status Change Select */}

        <div className="w-full sm:w-auto sm:ml-auto">
          <Select
            value={currentStatus}
            onValueChange={handleStatusChange}
            disabled={
              isSelectDisabled ||
              order.status === "cancelled" ||
              order.status === "completed"
            }
          >
            <SelectTrigger className="w-full sm:w-[180px] lg:w-[200px]">
              <SelectValue placeholder="Cambiar estado" />
            </SelectTrigger>
            <SelectContent>
              {statuses.map((status) => (
                <SelectItem key={status.db_name} value={status.db_name}>
                  {status.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </section>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-white max-w-sm sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar cambio de estado</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que quieres cambiar el estado del pedido de{" "}
              <strong>{getStatusName(dialogFromStatus)}</strong> a{" "}
              <strong>
                {selectedStatus ? getStatusName(selectedStatus) : ""}
              </strong>
              ?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:gap-0">
            <Button
              variant="outline"
              onClick={handleCancelStatusChange}
              className="w-full sm:w-auto"
            >
              Cancelar
            </Button>
            {selectedStatus === "cancelled" ? (
              <OrderCancellationModal
                trigger={
                  <Button className="bg-red-500 min-w-32 text-white hover:text-white hover:bg-red-600 w-full sm:w-auto">
                    Confirmar cancelación
                  </Button>
                }
                order={order as any}
                onCancellationSuccess={() => {
                  setIsDialogOpen(false);
                  setShouldDisableAfterClose(true);
                  // Defer applying cancelled until after dialog closes
                  setPendingStatusToApply("cancelled");
                }}
                onOrderStatusUpdate={() => {}}
              />
            ) : (
              <Button
                onClick={handleConfirmStatusChange}
                variant="default"
                className="bg-indigo-700 min-w-32 text-white hover:text-white hover:bg-indigo-600 w-full sm:w-auto"
                disabled={loading || order.status === "cancelled"}
              >
                {loading ? "Actualizando..." : "Confirmar cambio"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
